import { Attribute, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, SimpleChanges } from "@angular/core";
import { TranslatedString } from 'models/translated-string';
import { syncButtonNameFromHost } from 'directives/cs-capture-cover/host-name-source';

@Component({
    selector: 'cs-button',
    templateUrl: 'cs-button.html',
    host: { 'class': 'button' },
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CsButtonComponent {

  @Input() icon: string;
  @Input() label?: TranslatedString;
  /**
   * The name a screen reader reads for this button, for a button whose only content is an
   * icon. It is bound onto the inner `<button>` rather than left on the host: the host is a
   * plain custom element with no ARIA role, and an `aria-label` there is ignored by most
   * screen readers, so a caller writing `[attr.aria-label]` on `<cs-button>` gets a control
   * with no name at all.
   *
   * A button with visible text needs nothing here: without it the inner `<button>` takes its
   * name from the host's visible text through `aria-labelledby` (see `syncButtonNameFromHost`).
   */
  @Input() ariaLabel?: string;
  @Input() disabled: boolean = false;
  @Input() type: string = "button";
  @Input() fillIcon: boolean = undefined;
  @Input('icon-right') public iconRight: string;
  /**
   * An icon drawn at the button's right edge while the label stays centred, beside a leading
   * `icon` (`icon-right` moves the one icon instead, so it cannot draw both). Decorative: it is
   * hidden from screen readers and is not a tap target of its own.
   */
  @Input() endIcon?: string;
  @Input() toggle: boolean | undefined;
  /**
   * The screen-reader name of the right-hand icon as its own button, e.g. "Remove Recipes
   * filter". The icon is a separate button only while this is set AND `iconRightClick` is bound,
   * so one chip can show a plain caret in one state and a tappable × in another.
   */
  @Input() iconRightLabel?: string;
  /**
   * Makes the right-hand icon a separate tap target, for a chip whose × does something other
   * than the chip itself (the Add Foods type chip: the name opens a picker, the × goes back to
   * All, #5902). Only a caller that binds this and sets `iconRightLabel` gets the separate
   * button, so every other cs-button renders as before. The click stops here and does not reach
   * the host's own (click) handler.
   */
  @Output() iconRightClick = new EventEmitter<Event>();

  /** true when the right-hand icon is drawn as its own button rather than as part of this one */
  protected get iconRightIsButton(): boolean {
    return this.iconRightClick.observed && !!this.iconRightLabel;
  }

  /** keeps the tap from reaching the host's own (click), then reports it to the caller */
  protected handleIconRightClick(event: Event) {
    event.stopPropagation();
    this.iconRightClick.emit(event);
  }

  constructor(
    @Attribute('secondary') public secondary: string,
    @Attribute('tertiary') public tertiary: string,
    @Attribute('x-small') public xSmall: string,
    @Attribute('small') public small: string,
    @Attribute('medium') public medium: string,
    @Attribute('large') public large: string,
    public elementRef: ElementRef,
    private cdr: ChangeDetectorRef) {

  }

  private contentObserver: MutationObserver = undefined;

  ngOnChanges(changes: SimpleChanges) {
    if (changes.icon || changes.label) {
      this.refreshClasses();
    }
    if (changes.disabled) {
      this.refreshDisabledAttribute();
    }
    if (changes.toggle) {
      this.refreshToggleAttribute();
    }
  }

  refreshToggleAttribute() {
    // when toggle is defined, dynamically control primary/secondary styling
    if (this.toggle === undefined) return;

    const element = this.elementRef.nativeElement as HTMLElement;
    if (this.toggle) {
      // selected state → primary (remove secondary, add selected class)
      element.removeAttribute('secondary');
      element.classList.add('selected');
    } else {
      // unselected state → secondary (remove selected class)
      element.setAttribute('secondary', '');
      element.classList.remove('selected');
    }
  }

  hasIcon(element: HTMLElement): boolean {
    for (let i = 0; i < element.children.length; i++) {
      let child = element.children[i];
      if (child.tagName === 'CS-ICON' || child.tagName === 'CF-SPINNER') {
        return true;
      }
    }
    return false;
  }

  hasLabel(element: HTMLElement): boolean {
    for (let i = 0; i < element.children.length; i++) {
      let child = element.children[i];
      if (child.tagName === 'CS-LABEL') {
        return true;
      }
    }
    return element.textContent.trim() !== '';
  }

  refreshDisabledAttribute() {
    let element = this.elementRef.nativeElement as HTMLElement;
    if (this.disabled) {
      element.setAttribute('disabled', '');
    } else {
      element.removeAttribute('disabled');
    }
  }

  private pendingMutationUpdate = false;

  ngOnInit() {
    this.refreshDisabledAttribute();
    const config = { childList: true, subtree: true };
    this.contentObserver = new MutationObserver((mutationList, observer) => {
      void observer;
      for (const mutation of mutationList) {
        if (mutation.type === "childList") {
          this.refreshClasses();
          // Batch mutation updates to avoid excessive change detection
          if (!this.pendingMutationUpdate) {
            this.pendingMutationUpdate = true;
            void Promise.resolve().then(() => {
              this.cdr.markForCheck();
              this.pendingMutationUpdate = false;
            });
          }
        }
      }
    });

    this.contentObserver.observe(this.elementRef.nativeElement, config);
  }

  ngOnDestroy() {
    if (this.contentObserver) {
      this.contentObserver.disconnect();
    }
  }

  refreshClasses() {
    let element = this.elementRef.nativeElement as HTMLElement;
    let hasIcon = this.hasIcon(element);
    let hasLabel = this.hasLabel(element);
    if ((!this.label && !hasLabel) && (this.icon || hasIcon)) {
      element.classList.add('icon-only');
    } else {
      element.classList.remove('icon-only');
    }
    if ((this.label || hasLabel) && (!this.icon && !hasIcon)) {
      element.classList.add('label-only');
    } else {
      element.classList.remove('label-only');
    }
  }

  ngAfterViewChecked() {
    // the inner <button> is empty, so without an ariaLabel it takes its name from the drawn text
    syncButtonNameFromHost(this.elementRef, this.ariaLabel);
  }

  ngAfterContentInit() {
    this.refreshClasses();
  }
}